import { useEffect, useState } from "react"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { useLocalization } from "@fluent/react"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { selectSettings } from "../../settings/settingsSlice"
import { ErrorContainer } from "../../../app/ErrorContainer"
import { APIError } from "../../../app/appTypes"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { Box, Checkbox, IconButton, Tooltip } from "@mui/material"
import { unwrapResult } from "@reduxjs/toolkit"
import { CompanyDocumentType, deleteCompanyDocumentType, loadCompanyDocumentTypes } from "./companyDocumentTypesApi"
import { FlexDiv } from "../../../app/Utils"
import { AddFabButton } from "../../../app/components/AddFabButton"
import { AddEditCompanyDocumentTypeDialog } from "./dialogs/AddEditCompanyDocumentTypeDialog"
import { DeleteConfirmationDialog } from "../../utils/DeleteConfirmationDialog"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import SettingsIcon from "@mui/icons-material/Settings"
import { DocumentTypeCodeBadge } from "./components/DocumentTypeCodeBadge"
import { QmsSettingsDialog } from "./dialogs/QmsSettingsDialog"

export const CompanyDocumentTypesList = () => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings)

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [loadingError, setError] = useState<APIError | undefined>(undefined)
    const [loadingErrorOccured, setLoadingErrorOccured] = useState<boolean>(false)
    const [documentTypes, setDocumentTypes] = useState<CompanyDocumentType[]>([])
    const [createDialogVisible, setCreateDialogVisible] = useState<boolean>(false)
    const [editDialogVisible, setEditDialogVisible] = useState<boolean>(false)
    const [selectedDocumentType, setSelectedDocumentType] = useState<CompanyDocumentType | undefined>(undefined)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [itemToDeleteId, setItemToDeleteId] = useState<number | undefined>(undefined)
    const [qmsDialogOpen, setQmsDialogOpen] = useState(false)

    const getCustomLocaleText = () => {
        if (locale === "pl") {
            return plPL.components.MuiDataGrid.defaultProps.localeText
        }
        return enUS.components.MuiDataGrid.defaultProps.localeText
    }

    const handleEditClick = (documentType: CompanyDocumentType) => {
        setSelectedDocumentType(documentType)
        setEditDialogVisible(true)
    }

    const handleDeleteClick = (id: number) => {
        setItemToDeleteId(id)
        setDeleteDialogOpen(true)
    }

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false)
        setItemToDeleteId(undefined)
    }

    const handleQmsClick = (documentType: CompanyDocumentType) => {
        setSelectedDocumentType(documentType)
        setQmsDialogOpen(true)
    }

    const handleQmsClose = () => {
        setQmsDialogOpen(false)
        setSelectedDocumentType(undefined)
    }

    const isQmsType = (documentType: CompanyDocumentType) => documentType.isQmsEnabled

    const handleDeleteConfirm = async () => {
        if (!itemToDeleteId) return

        const resultAction = await dispatch(deleteCompanyDocumentType(itemToDeleteId))

        if (deleteCompanyDocumentType.fulfilled.match(resultAction)) {
            setDocumentTypes(prev => prev.filter(x => x.id !== itemToDeleteId))
            setDeleteDialogOpen(false)
            setItemToDeleteId(undefined)
        } else {
            console.error(resultAction.payload)
        }
    }

    const onSucces = (documentType: CompanyDocumentType) => {
        setCreateDialogVisible(false)
        setEditDialogVisible(false)
        setSelectedDocumentType(undefined)

        setDocumentTypes(prev => {
            const exists = prev.some(x => x.id === documentType.id)
            if (exists) {
                return prev.map(x => (x.id === documentType.id ? documentType : x))
            }
            return [...prev, documentType].sort((a, b) => a.code.localeCompare(b.code))
        })
    }

    const handleHideDialog = () => {
        setCreateDialogVisible(false)
        setEditDialogVisible(false)
        setSelectedDocumentType(undefined)
    }

    useEffect(() => {
        setIsLoading(true)
        setLoadingErrorOccured(false)
        setError(undefined)

        dispatch(loadCompanyDocumentTypes({ includeInactive: true }))
            .then(unwrapResult)
            .then(data => setDocumentTypes(data))
            .catch((error: APIError) => {
                console.error(error)
                setError(error)
                setLoadingErrorOccured(true)
            })
            .finally(() => setIsLoading(false))
    }, [dispatch])

    const columns: GridColDef<CompanyDocumentType>[] = [
        {
            field: "code",
            headerName: l10n.getString("company-document-type-code", null, "_Kod"),
            width: 100,
            align: "center",
            headerAlign: "center",
            renderCell: params => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <DocumentTypeCodeBadge code={params.row.code} color={params.row.color} />
                </Box>
            ),
        },
        {
            field: "title",
            headerName: l10n.getString("company-document-type-title", null, "_Tytuł"),
            flex: 1,
            minWidth: 200,
        },
        {
            field: "description",
            headerName: l10n.getString("description", null, "_Opis"),
            flex: 1,
            minWidth: 200,
        },
        {
            field: "displayOrder",
            headerName: l10n.getString("company-document-type-display-order", null, "_Kolejność"),
            width: 200,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "color",
            headerName: l10n.getString("company-document-type-color", null, "_Kolor"),
            width: 90,
            align: "center",
            headerAlign: "center",
            renderCell: params => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            bgcolor: params.value || "grey.400",
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    />
                </Box>
            ),
        },
        {
            field: "isActive",
            headerName: l10n.getString("company-document-type-is-active", null, "_Aktywny"),
            width: 120,
            align: "center",
            headerAlign: "center",
            renderCell: params => (
                <Checkbox
                    checked={!!params.value}
                    disabled
                    sx={{
                        color: params.value ? "success.main" : "grey.400",
                        "&.Mui-checked": { color: "success.main" },
                        "&.Mui-disabled": { color: params.value ? "success.main" : "grey.400" },
                    }}
                />
            ),
        },
        {
            field: "isQms",
            headerName: "Czy QMS",
            width: 120,
            align: "center",
            headerAlign: "center",
            sortable: false,
            filterable: false,
            valueGetter: (_, row) => isQmsType(row),
            renderCell: params => (
                <Checkbox
                    checked={!!params.value}
                    disabled
                    sx={{
                        color: params.value ? "success.main" : "grey.400",
                        "&.Mui-checked": { color: "success.main" },
                        "&.Mui-disabled": { color: params.value ? "success.main" : "grey.400" },
                    }}
                />
            ),
        },
        {
            field: "actions",
            headerName: l10n.getString("actions", null, "_Akcje"),
            width: 170,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: params => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%" }}>
                    <Tooltip title={l10n.getString("edit", null, "_Edytuj")}>
                        <IconButton size="small" onClick={() => handleEditClick(params.row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Ustawienia QMS">
                        <IconButton
                            size="small"
                            color="primary"
                            disabled={!isQmsType(params.row)}
                            onClick={() => handleQmsClick(params.row)}
                        >
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={l10n.getString("delete", null, "_Usuń")}>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ]

    if (isLoading) return <LoadingContainer />
    if (loadingErrorOccured) return <ErrorContainer error={loadingError} />

    return (
        <>
            <DataGrid
                localeText={getCustomLocaleText()}
                style={{ minHeight: 100, height: "calc(100vh - 300px)" }}
                rows={documentTypes}
                columns={columns}
                initialState={{
                    sorting: { sortModel: [{ field: "displayOrder", sort: "asc" }] },
                }}
            />

            <FlexDiv>
                <AddFabButton onClick={() => setCreateDialogVisible(true)} />
            </FlexDiv>

            {createDialogVisible && (
                <AddEditCompanyDocumentTypeDialog
                    mode="create"
                    nextDisplayOrder={(Math.max(0, ...documentTypes.map(x => x.displayOrder))) + 1}
                    onSuccessfullySaved={onSucces}
                    onCancel={handleHideDialog}
                />
            )}

            {editDialogVisible && selectedDocumentType && (
                <AddEditCompanyDocumentTypeDialog
                    mode="edit"
                    documentType={selectedDocumentType}
                    onSuccessfullySaved={onSucces}
                    onCancel={handleHideDialog}
                />
            )}

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />

            <QmsSettingsDialog
                open={qmsDialogOpen}
                documentTypeId={selectedDocumentType?.id}
                settings={selectedDocumentType?.qmsSettings}
                onClose={handleQmsClose}
                onSuccessfullySaved={savedSettings => {
                    if (!selectedDocumentType) return

                    const updatedDocumentType = { ...selectedDocumentType, qmsSettings: savedSettings }
                    setDocumentTypes(prev => prev.map(x => (x.id === updatedDocumentType.id ? updatedDocumentType : x)))
                    setSelectedDocumentType(updatedDocumentType)
                }}
            />
        </>
    )
}
